import { DataGrid } from "@/components/DataGrid";
import { type ClientDmUsageResponse } from "@/features/accountSettings/api";
import { Box } from "@mui/material";
import {
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { format } from "date-fns";
import { useMemo } from "react";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface ClientDmUsageHeaders
  extends Omit<GridColDef, "field" | "headerName" | "width"> {
  field: keyof ClientDmUsageRow;
  headerName: string;
  width: number;
}

interface ClientDmUsageRow {
  id: number;
  userName: string;
  date: string;
  memo: string;
  quantity: number;
  price: number;
  amount: number;
}

interface Props {
  dmUsages: ClientDmUsageResponse[];
}

const column: ClientDmUsageHeaders[] = [
  {
    field: "userName",
    headerName: "ユーザー",
    width: 220,
  },
  {
    field: "date",
    headerName: "利用日",
    width: 180,
  },
  {
    field: "memo",
    headerName: "内容",
    width: 320,
    // 管理画面で入力した費目は改行を含む場合があるため、折り返して表示する
    renderCell: ({ value }: GridRenderCellParams<string, ClientDmUsageRow>) => (
      <Box sx={{ whiteSpace: "pre-line" }}>{value}</Box>
    ),
  },
  {
    field: "quantity",
    headerName: "数量",
    width: 120,
    renderCell: ({ value }: GridRenderCellParams<number, ClientDmUsageRow>) =>
      formatNumberWithComma(value),
  },
  {
    field: "price",
    headerName: "単価",
    width: 120,
    renderCell: ({ value }: GridRenderCellParams<number, ClientDmUsageRow>) =>
      formatNumberWithComma(value),
  },
  {
    field: "amount",
    headerName: "金額",
    width: 120,
    renderCell: ({ value }: GridRenderCellParams<number, ClientDmUsageRow>) =>
      formatNumberWithComma(value),
  },
];

export const ClientDmUsageGrid: React.FC<Props> = (props) => {
  const { dmUsages } = props;
  const rows = useMemo(
    () =>
      dmUsages.map<ClientDmUsageRow>((dmUsage) => {
        return {
          id: dmUsage.id,
          userName: dmUsage.userName,
          date: format(new Date(dmUsage.createdAt), "yyyy/MM/dd"),
          memo: dmUsage.memo,
          quantity: dmUsage.quantity,
          price: dmUsage.price,
          amount: dmUsage.amount,
        };
      }),
    [dmUsages]
  );
  return (
    <DataGrid
      rows={rows}
      columns={column}
      getRowHeight={() => "auto"}
      sx={{ "& .MuiDataGrid-cell": { py: 1 } }}
    ></DataGrid>
  );
};
